import { useState } from "react";
import { RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/http";

/** Spaced-repetition quiz: re-asks a card that is ≥7 days old. */
export function LearnReviewCard({ due, lang, t, onAnswered }) {
  const [card, setCard] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [left, setLeft] = useState(due);

  const load = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.get(`/client/learn/review?lang=${lang || "it"}`);
      setLeft(data.due);
      setCard(data.pill);
      if (!data.pill) toast.info(t("learn.reviewNone"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally { setBusy(false); }
  };

  const answer = async (i) => {
    if (busy || result) return;
    setBusy(true);
    try {
      const { data } = await api.post("/client/learn/review-answer", { pill_id: card.id, answer_index: i });
      setResult({ ...data, picked: i });
      setLeft(data.remaining);
      if (data.correct) toast.success(data.awarded ? t("learn.reviewCorrect", { points: data.points }) : t("learn.quizCorrect"));
      if (data.streak_bonus) toast.success(t("learn.streakBonus", { points: data.streak_bonus, n: data.streak }));
      onAnswered?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally { setBusy(false); }
  };

  if (!card && !left) return null;

  return (
    <div className="mb-3 rounded-xl bg-white/60 dark:bg-white/5 border border-[#5A8B8F]/20 p-3" data-testid="learn-review">
      <div className="flex items-center justify-between gap-2 mb-1">
        <p className="text-[10px] font-bold uppercase tracking-widest text-[#1E5C2E]/70 dark:text-green-200/70">
          {t("learn.reviewTitle")}
        </p>
        {left > 0 && (
          <span className="text-[10px] font-semibold rounded-full bg-[#3AA35B]/15 text-[#1E5C2E] dark:text-green-200 px-2 py-0.5" data-testid="learn-review-due">
            {t("learn.reviewDue", { n: left })}
          </span>
        )}
      </div>
      {!card ? (
        <button
          type="button" onClick={load} disabled={busy} data-testid="learn-review-start"
          className="w-full flex items-center justify-center gap-2 rounded-lg bg-[#3AA35B] text-white text-sm font-semibold px-3 py-2 hover:bg-[#2f8c4c] disabled:opacity-50 transition"
        >
          <RotateCcw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />
          {t("learn.reviewStart")}
        </button>
      ) : (
        <div data-testid="learn-review-card">
          <p className="text-[11px] text-[#1E5C2E]/75 dark:text-green-200/70 mb-1">{t("learn.reviewHint", { days: card.days_ago })}</p>
          <p className="font-heading text-sm font-bold text-[#1A1D1E] dark:text-slate-50 mb-1">{card.emoji} {card.title}</p>
          <p className="text-sm font-semibold text-[#1A1D1E] dark:text-slate-50 leading-snug mb-2">{card.quiz.question}</p>
          <div className="space-y-1.5">
            {card.quiz.options.map((opt, i) => {
              const answered = !!result;
              const isPicked = result?.picked === i;
              const isCorrect = result?.correct_index === i;
              let cls = "bg-white/80 dark:bg-white/10 text-[#1A1D1E] dark:text-slate-100 border-transparent hover:bg-white";
              if (answered && isCorrect) cls = "bg-emerald-100 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-200 border-emerald-400";
              else if (answered && isPicked) cls = "bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-200 border-rose-400";
              else if (answered) cls = "bg-white/40 dark:bg-white/5 text-slate-400 border-transparent";
              return (
                <button
                  key={i} type="button" onClick={() => answer(i)} disabled={answered || busy}
                  data-testid={`learn-review-option-${i}`}
                  className={`w-full text-left rounded-lg border px-3 py-2 text-sm font-medium transition ${cls} disabled:cursor-default`}
                >
                  {answered && isCorrect ? "✓ " : answered && isPicked ? "✗ " : ""}{opt}
                </button>
              );
            })}
          </div>
          {result && (
            <div className="mt-2" data-testid="learn-review-feedback">
              <p className={`text-xs leading-snug ${result.correct ? "text-emerald-700 dark:text-emerald-300" : "text-rose-600 dark:text-rose-300"}`}>
                {result.correct ? t("learn.reviewCorrect", { points: result.points }) : t("learn.reviewWrong")}
                {result.explanation ? ` ${result.explanation}` : ""}
              </p>
              {result.takeaway && <p className="text-xs italic text-[#1E5C2E] dark:text-green-200 mt-1">“{result.takeaway}”</p>}
              {left > 0 && (
                <button type="button" onClick={load} disabled={busy} data-testid="learn-review-next"
                  className="mt-2 text-xs font-semibold text-[#1E5C2E] dark:text-green-200 underline underline-offset-2">
                  {t("learn.reviewNext", { n: left })}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
